<template>
  <div v-if="showPaymentData">
    <Card :loadingState="loading">
      <template #content>
        <Collapser
          id="policy.toggle-pay-data"
          :collapserTitle="$t('payment.paymentData')"
          :expanded="true"
          trackId="togglePayData"
        >
          <template #collapserContent>
            <p v-if="hasError" class="p">{{ $t('payment.errorText') }}</p>
            <DataBox
              v-else
              :data="databoxData"
              @click:icon="navigateToIconLink"
            ></DataBox>
          </template>
        </Collapser>
      </template>
    </Card>
  </div>
</template>

<script lang="ts" setup>
import {
  Card,
  Collapser,
  type DataBlock,
  DataBox,
  type IconDataSpan
} from 'de-zurich-ui-library'
import { computed, onMounted, type Ref, ref } from 'vue'
import { navigate } from '@/utils/NavigationUtils'
import { usePoliciesStore } from '@/stores/policiesStore'
import type {
  LebenVersicherungsVertrag,
  LebenVertragKomponente,
  ZusatzKomponente
} from 'ods-api'
import { type PaymentData, PaymentPeriod } from 'policies-api'
import { StringUtils } from '@/utils/policy/StringUtils'
import { ContractUtils } from '@/utils/policy/ContractUtils'
import { ObjectUtils } from '@/utils/policy/ObjectUtils'
import { floatEquals } from '@/utils/policy/NumberUtils'
import { useI18n } from 'vue-i18n'
import { formatMonetaryValue } from '@/utils/utils'

const { t } = useI18n()

const props = defineProps({
  policyId: {
    type: String,
    required: true
  }
})

// Icon data
const changeBankDataURL: string =
  globalThis.ENV_CONFIG?.SERVICE_LINKS.PERSONAL_PAGE.CHANGE_BANKDATA_PAGE

const bankIconDataSpan: IconDataSpan = {
  iconClass: 'icon-bankdaten',
  href: changeBankDataURL,
  label: t('payment.icon.changeBankData').toString()
}

const databoxData: Ref<DataBlock[]> = ref([])
const hasError = ref(false)
const loading = ref(true)
const odsContract: Ref<LebenVersicherungsVertrag | null> = ref(null)
const odsContractId: Ref<string | null> = ref(null)
const paymentData: Ref<PaymentData | null> = ref(null)

const showPaymentData = computed(() => {
  return StringUtils.isNotEmpty(odsContractId.value) ? !isOdsContractInRetirement() : true
})

onMounted(async () => {
  // get full policy from DDS Data
  usePoliciesStore()
    .getPolicyByPolicyId(props.policyId)
    .then(async (responsePolicy) => {
      paymentData.value = responsePolicy?.paymentData ?? null
      odsContractId.value = responsePolicy?.odsContractId ?? null
      if (!odsContractId.value || odsContractId.value === 'null') return
      await loadOdsData()
    })
    .catch(() => {
      paymentData.value = null
      hasError.value = true
    })
    .finally(() => {
      prepareDataboxData()
      loading.value = false
    })
})

function fillPaymentDataWithOdsContract(contract: LebenVersicherungsVertrag) {
  const zahlBeitrag = contract.zahlBeitrag
  if (!paymentData.value || !zahlBeitrag) {
    return
  }

  const beitrag = showReducedPremium()
    ? zahlBeitrag.beitragGemaessZahlweiseErmaessigt
    : zahlBeitrag.beitragGemaessZahlweise

  if (beitrag) {
    paymentData.value.grossPremiumBasedOnPaymentPeriod = {
      amount: beitrag,
      currency: 'EUR'
    }
  }

  if (zahlBeitrag?.zahlweise?.code) {
    // ODS API: zahlBeitrag.zahlweise.wert can have the following values
    // jährlich, monatlich, halbjährlich, vierteljährlich, Einmalbeitrag
    // use switch to convert ods to dds type in a save way ¯\_(ツ)_/¯
    switch (zahlBeitrag.zahlweise.wert) {
      case 'jährlich':
        paymentData.value.paymentPeriod = PaymentPeriod.Yearly
        break
      case 'monatlich':
        paymentData.value.paymentPeriod = PaymentPeriod.Monthly
        break
      case 'halbjährlich':
        paymentData.value.paymentPeriod = PaymentPeriod.HalfYearly
        break
      case 'vierteljährlich':
        paymentData.value.paymentPeriod = PaymentPeriod.Quarterly
        break
      case 'Einmalbeitrag':
        paymentData.value.paymentPeriod = PaymentPeriod.SinglePremium
        break
      default:
        paymentData.value.paymentPeriod = PaymentPeriod.Other
    }
  }
}

function hasFoundContributionOffsetting(): boolean {
  if (!odsContract.value?.hauptVersicherung) {
    return false
  }
  const mainContract = ContractUtils.getLebensvertragsKomponente(
    odsContract.value.hauptVersicherung
  )

  if (!mainContract) {
    return false
  }

  if (isContributionOffsettingPresent(mainContract)) {
    return true
  }

  const supplementaryContract = odsContract.value?.zusatzVersicherungen

  if (!supplementaryContract || ObjectUtils.isEmpty(supplementaryContract)) {
    return false
  }

  return supplementaryContract
    .map((component: ZusatzKomponente) =>
      ContractUtils.getLebensvertragsKomponenteFromSupplementaryComponent(component)
    )
    .some((supplementaryComponent) =>
      isContributionOffsettingPresent(supplementaryComponent)
    )
}

function isContributionOffsettingPresent(
  mainComponent: LebenVertragKomponente | null
): boolean {
  const CONTRIBUTION_OFFSETTING_CODE = 'BV'
  if (!mainComponent) {
    return false
  }
  const surplusAppropriation =
    mainComponent.ueberschussVerwendung?.verwendungBeitragsPflicht
  return (
    surplusAppropriation?.code?.toUpperCase() ===
    CONTRIBUTION_OFFSETTING_CODE.toUpperCase()
  )
}

function isOdsContractInRetirement(): boolean {
  const tarifArt = ContractUtils.getTarifArt(odsContract.value?.hauptVersicherung)
  return tarifArt.code === 'REN_U' || tarifArt.code === 'REN_S'
}

async function loadOdsData(): Promise<void> {
  if (!paymentData.value || !odsContractId.value) return
  odsContract.value = await usePoliciesStore().getOdsContractByContractId(
    odsContractId.value
  )
  fillPaymentDataWithOdsContract(odsContract.value)
}

function navigateToIconLink(href: string): void {
  navigate(href)
}

function getGrossPremiumText(
  showGrossPremiumInfoMessage: boolean,
  formattedGrossPremium: string
) {
  return showGrossPremiumInfoMessage
    ? String(t('payment.grossPremiumInfoMessage'))
    : formattedGrossPremium
}

function getGrossPremiumLabel() {
  return showReducedPremium()
    ? String(t('payment.grossPremiumReduced'))
    : String(t('payment.grossPremiumBasedOnPaymentPeriod'))
}

function isPaymentProcedureMissing(val: string): boolean {
  return (
    paymentData.value !== null &&
    (!paymentData.value.paymentProcedure || !(val in paymentData.value.paymentProcedure))
  )
}

function prepareDataboxData(showGrossPremiumInfoMessage = false) {
  if (!paymentData.value) return

  const formattedGrossPremium: string = formatMonetaryValue(
    paymentData.value.grossPremiumBasedOnPaymentPeriod
  )

  // Since showGrossPremiumInfoMessage is always false, this is useless for now
  // It is currently not certain whether this will ever be used
  const grossPremiumText: string = getGrossPremiumText(
    showGrossPremiumInfoMessage,
    formattedGrossPremium
  )

  databoxData.value = [
    {
      data: [
        {
          label: String(t('payment.paymentMethodLabel')),
          value: String(t('payment.paymentMethod.' + paymentData.value.paymentMethod)),
          iconSpan:
            paymentData.value.paymentMethod === 'Invoice' ? bankIconDataSpan : undefined,
          hide: !paymentData.value.paymentMethod
        },
        {
          label: getGrossPremiumLabel(),
          value: grossPremiumText,
          hide:
            !paymentData.value.grossPremiumBasedOnPaymentPeriod &&
            !showGrossPremiumInfoMessage
        },
        {
          label: String(t('payment.paymentPeriodLabel')),
          value: t('payment.paymentPeriod.' + paymentData.value.paymentPeriod),
          hide: !paymentData.value.paymentPeriod
        },
        {
          label: String(t('payment.iban')),
          value:
            paymentData.value.paymentProcedure &&
            'iban' in paymentData.value.paymentProcedure
              ? (paymentData.value.paymentProcedure.iban as string)
              : '',
          iconSpan: bankIconDataSpan,
          hide: isPaymentProcedureMissing('iban')
        },
        {
          label: String(t('payment.routingNumber')),
          value:
            paymentData.value.paymentProcedure &&
            'routingNumber' in paymentData.value.paymentProcedure
              ? (paymentData.value.paymentProcedure.routingNumber as string)
              : '',
          iconSpan: bankIconDataSpan,
          hide: isPaymentProcedureMissing('routingNumber')
        },
        {
          label: String(t('payment.cardHolderName')),
          value:
            paymentData.value.paymentProcedure &&
            'cardHolderName' in paymentData.value.paymentProcedure
              ? (paymentData.value.paymentProcedure.cardHolderName as string)
              : '',
          hide: isPaymentProcedureMissing('cardHolderName')
        }
      ]
    }
  ]
}

function showReducedPremium() {
  if (!odsContract.value?.zahlBeitrag?.beitragGemaessZahlweiseErmaessigt) return false

  return (
    hasFoundContributionOffsetting() &&
    !floatEquals(
      odsContract.value.zahlBeitrag.beitragGemaessZahlweise,
      odsContract.value.zahlBeitrag.beitragGemaessZahlweiseErmaessigt
    )
  )
}
</script>
